import { ReactElement } from 'react';
import Loader from '../components/loader';

type ButtonTheme =
  | 'primary'
  | 'secondary-gray'
  | 'secondary-success'
  | 'secondary-error'
  | 'secondary-color'
  | 'primary-purple'
  | 'tertiary-gray'
  | 'tertiary-color'
  | 'danger';
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
type ButtonControlProps = {
  name: string;
  theme: ButtonTheme;
  size: ButtonSize;
  disabled?: boolean;
  buttonType?: 'submit' | 'button';
  isBlockButton?: boolean;
  onClick?: (e: any) => void;
  className?: string;
  loading?: boolean;
  form?: string;
};

/**
 * Function which returns the css classes for a button based on the theme.
 * @param theme - Theme of the button.
 * @returns - Classes of button.
 */
const getThemeClasses = (theme: ButtonTheme): string => {
  let themeClasses = '';

  if (theme === 'primary') {
    themeClasses =
      'bg-primary-500 text-white border border-primary-500 hover:bg-primary-700 hover:border-primary-700 focus:ring-4 focus:ring-primary-100 disabled:bg-primary-200 disabled:border-primary-200';
  } else if (theme === 'secondary-gray') {
    themeClasses =
      'bg-gray-300 dark:bg-white border border-gray-400 text-gray-700 hover:bg-gray-50 hover:dark:bg-gray-50 hover:text-gray-800 focus:text-gray-800 focus:ring-4 focus:ring-gray-100 disabled:bg-gray-200 disabled:text-gray-400';
  } else if (theme === 'secondary-color') {
    themeClasses =
      'border border-primary-200 text-primary-200 hover:bg-primary-100 hover:text-primary-500 focus:text-primary-500 focus:ring-4 focus:ring-primary-50 focus:bg-primary-50 focus:hover:bg-primary-100 disabled:opacity-60';
  } else if (theme === 'primary-purple') {
    themeClasses = 'border border-pink bg-pink text-white text-xs font-normal';
  } else if (theme === 'tertiary-gray') {
    themeClasses =
      'text-gray-300 text-xs font-normal hover:bg-gray-300 hover:dark:bg-white hover:text-gray-700';
  } else if (theme === 'tertiary-color') {
    themeClasses =
      'text-primary-500 text-xs font-normal hover:bg-primary-50 hover:text-primary-800';
  } else if (theme === 'danger') {
    themeClasses =
      'border border-error-500 text-error-500 hover:bg-error-500 hover:text-gray-300 focus:ring-4 focus:ring-error-500 focus:bg-primary-50 focus:hover:bg-error-500';
  } else if (theme === 'secondary-error') {
    themeClasses =
      'border border-error-500 text-error-500 hover:bg-error-100 hover:text-error-700 focus:text-error-700 focus:ring-4 focus:ring-error-50 focus:bg-error-50 focus:hover:bg-error-100 disabled:opacity-60';
  } else if (theme === 'secondary-success') {
    themeClasses =
      'border border-success-500 text-success-500 hover:bg-success-100 hover:text-success-700 focus:text-success-700 focus:ring-4 focus:ring-success-50 focus:bg-success-50 focus:hover:bg-success-100 disabled:opacity-60';
  }
  return themeClasses;
};

/**
 * Function which returns the css classes for a button based on the size.
 * @param size - Size of the button.
 * @param isBlockButton - Flag to make the button block size.
 * @returns - Classes of button.
 */
const getSizeClasses = (size: ButtonSize, isBlockButton: boolean): string => {
  let sizeClasses = '';

  if (size === 'sm') {
    sizeClasses = 'px-3.5 py-2 font-semibold text-sm min-h-[2.25rem] min-w-[10.125rem]';
  } else if (size === 'md') {
    sizeClasses = 'px-4 py-2.5 font-semibold text-sm min-h-[2.5rem] min-w-[10.375rem]';
  } else if (size === 'lg') {
    sizeClasses = 'px-[1.125rem] py-2.5 font-semibold text-base min-h-[2.75rem] min-w-[11.313rem]';
  } else if (size === 'xl') {
    sizeClasses = 'px-5 py-3 font-semibold text-base min-h-[3rem] min-w-[11.563rem]';
  } else if (size === '2xl') {
    sizeClasses = 'px-7 py-4 font-semibold text-lg min-h-[3.75rem] min-w-[14.25rem]';
  } else if (size === 'xs') {
    sizeClasses = 'px-3.5 py-1 font-semibold text-xs';
  }

  return sizeClasses + (isBlockButton ? ' w-full' : '');
};

/**
 * Button component which renders based on props.
 * @param props - Properties of the button component.
 * @returns - Button Component.
 */
export default function Button(props: ButtonControlProps): ReactElement {
  const {
    name,
    theme,
    size,
    disabled = false,
    onClick,
    buttonType = 'button',
    isBlockButton = false,
    className,
    loading = false,
    form
  } = props;

  return (
    <button
      type={buttonType === 'submit' ? 'submit' : 'button'}
      className={`${getThemeClasses(theme)} ${getSizeClasses(size, isBlockButton)} ${
        className && className
      } rounded shadow-xs whitespace-nowrap relative ${
        disabled ? 'cursor-not-allowed' : 'cursor-pointer'
      }`}
      disabled={disabled || loading}
      onClick={onClick}
      aria-label={name}
      form={form}
    >
      {loading ? <Loader color="#fff" type="clip" size={16} /> : name}
    </button>
  );
}
